/**
 * Pure resolver for the single-switch backend control plane. Given the dev-mode
 * gate, the live pointer (or null on fetch failure), the last cached pointer, and
 * the baked prod target, decide which backend to use. No I/O — fully testable.
 */
export type ActiveEnv = 'prod' | 'dev';

export interface DevPointer {
  activeEnv: ActiveEnv;
  devUrl: string | null;
  devAnonKey: string | null;
}

export interface BackendTarget {
  url: string;
  anonKey: string;
  headers: Record<string, string>;
  isDev: boolean;
}

/** Tolerant narrowing of the raw `dev_runtime_config` row into a DevPointer. */
export function parseDevPointer(raw: unknown): DevPointer | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const activeEnv: ActiveEnv = r.active_env === 'dev' ? 'dev' : 'prod';
  const devUrl = typeof r.dev_url === 'string' && r.dev_url.trim() ? r.dev_url.trim() : null;
  const devAnonKey =
    typeof r.dev_anon_key === 'string' && r.dev_anon_key.trim() ? r.dev_anon_key.trim() : null;
  return { activeEnv, devUrl, devAnonKey };
}

/** ngrok free shows an interstitial; this header skips it. Empty for non-ngrok hosts. */
export function ngrokHeaders(url: string): Record<string, string> {
  return /\bngrok[-.]/i.test(url) ? { 'ngrok-skip-browser-warning': 'true' } : {};
}

export function resolveActiveBackend(args: {
  devModeEnabled: boolean;
  pointer: DevPointer | null;
  cached: DevPointer | null;
  prod: { url: string; anonKey: string };
}): BackendTarget {
  const prodTarget: BackendTarget = {
    url: args.prod.url,
    anonKey: args.prod.anonKey,
    headers: {},
    isDev: false,
  };
  if (!args.devModeEnabled) return prodTarget;
  const p = args.pointer ?? args.cached;
  if (p && p.activeEnv === 'dev' && p.devUrl && p.devAnonKey) {
    return { url: p.devUrl, anonKey: p.devAnonKey, headers: ngrokHeaders(p.devUrl), isDev: true };
  }
  return prodTarget;
}
